import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DilutionCalculator = () => {
  const [dilutionData, setDilutionData] = useState({
    initialConcentration: '',
    finalConcentration: '',
    finalVolume: '',
    stockVolume: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDilutionData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateDilution = () => {
    const { initialConcentration, finalConcentration, finalVolume } = dilutionData;
    
    const C1 = parseFloat(initialConcentration) || 0;
    const C2 = parseFloat(finalConcentration) || 0;
    const V2 = parseFloat(finalVolume) || 0;
    
    if (C1 === 0 || C2 === 0 || V2 === 0) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    if (C2 >= C1) {
      setError('Final concentration must be less than initial concentration');
      return;
    }

    const V1 = (C2 * V2) / C1;
    const diluentVolume = V2 - V1;

    const calculationSteps = [
      'Step 1: Using the dilution formula C1V1 = C2V2',
      `Initial Concentration (C1): ${C1} units/mL`,
      `Final Concentration (C2): ${C2} units/mL`,
      `Final Volume (V2): ${V2} mL`,
      '\nStep 2: Calculate required stock volume (V1)',
      `V1 = (C2 × V2) ÷ C1`,
      `V1 = (${C2} × ${V2}) ÷ ${C1}`,
      `V1 = ${V1.toFixed(2)} mL`,
      '\nStep 3: Calculate diluent volume',
      `Diluent Volume = Final Volume - Stock Volume`,
      `Diluent Volume = ${V2} - ${V1.toFixed(2)}`,
      `Diluent Volume = ${diluentVolume.toFixed(2)} mL`
    ];

    const newResults = {
      stockVolume: V1.toFixed(2),
      diluentVolume: diluentVolume.toFixed(2),
      finalVolume: V2,
      dilutionFactor: (C1 / C2).toFixed(2),
      calculationSteps
    };

    setResults(newResults);
    setDilutionData(prev => ({ ...prev, stockVolume: V1.toFixed(2) }));

    setChartData({
      labels: ['Stock Solution', 'Diluent', 'Final Solution'],
      datasets: [{
        label: 'Volume (mL)',
        data: [V1, diluentVolume, V2],
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(75, 192, 192, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  const exampleData = {
    initialConcentration: 100,
    finalConcentration: 20,
    finalVolume: 50,
    stockVolume: 10,
    diluentVolume: 40,
    dilutionFactor: 5
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Dilution Calculator | Laboratory Solution Dilution Tool</title>
          <meta name="description" content="Calculate the volumes needed for diluting solutions to specific concentrations. Determine the amount of stock solution and diluent required to achieve your target concentration." />
          <meta name="keywords" content="dilution calculator, solution dilution, laboratory calculations, concentration calculator, dilution factor" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8 ">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🧪</div>
            <h1 className="text-3xl font-bold text-gray-800">Dilution Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the volumes needed for diluting solutions to specific concentrations. This calculator helps determine the amount of stock solution and diluent required to achieve your target concentration.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial Concentration (units/mL):</label>
              <input
                type="number"
                name="initialConcentration"
                value={dilutionData.initialConcentration}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter initial concentration"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Final Concentration (units/mL):</label>
              <input
                type="number"
                name="finalConcentration"
                value={dilutionData.finalConcentration}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter target concentration"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Final Volume (mL):</label>
              <input
                type="number"
                name="finalVolume"
                value={dilutionData.finalVolume}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter desired final volume"
              />
            </div>
          </div>

          <button
            onClick={calculateDilution}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Required Volumes</h3>
                  <p className="text-lg">Stock Solution: {results.stockVolume} mL</p>
                  <p className="text-lg">Diluent: {results.diluentVolume} mL</p>
                  <p className="text-lg">Final Volume: {results.finalVolume} mL</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Dilution Factor</h3>
                  <p className="text-2xl font-bold">1:{results.dilutionFactor}</p>
                </div>
              </div>

              {chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Volume (mL)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Components'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Solution Components'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for diluting a solution:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Dilution Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Initial Concentration: {exampleData.initialConcentration} units/mL</li>
                <li>Final Concentration: {exampleData.finalConcentration} units/mL</li>
                <li>Final Volume Needed: {exampleData.finalVolume} mL</li>
              </ul>

              <h4 className="mb-4">Results:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Stock Solution Needed: {exampleData.stockVolume} mL</li>
                <li>Diluent Volume: {exampleData.diluentVolume} mL</li>
                <li>Dilution Factor: 1:{exampleData.dilutionFactor}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Use the dilution equation: C₁V₁ = C₂V₂
                  <ul className="list-disc pl-6">
                    <li>C₁ = Initial concentration</li>
                    <li>V₁ = Volume of stock solution needed</li>
                    <li>C₂ = Final concentration</li>
                    <li>V₂ = Final volume desired</li>
                  </ul>
                </li>
                <li>Solve for V₁ (stock volume needed):
                  <ul className="list-disc pl-6">
                    <li>V₁ = (C₂ × V₂) ÷ C₁</li>
                    <li>V₁ = ({exampleData.finalConcentration} × {exampleData.finalVolume}) ÷ {exampleData.initialConcentration} = {exampleData.stockVolume} mL</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Dilution Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Solution Data:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the initial concentration of your stock solution (units/mL)</li>
                  <li>Input the desired final concentration (units/mL)</li>
                  <li>Input the final volume needed (mL)</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>Required volume of stock solution</li>
                  <li>Required volume of diluent</li>
                  <li>Dilution factor</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated volumes</li>
                  <li>Check the dilution factor</li>
                  <li>View the visual representation of solution components</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Always use consistent units for concentrations</li>
                <li>Double-check your calculations</li>
                <li>Consider preparing extra volume for potential losses</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Dilutions <span className="text-blue-600">🔬</span></h2>
          <div className="text-gray-600">
            <p>
              Solution dilution is a fundamental laboratory technique with wide applications in:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Laboratory Research:</strong> Preparing solutions for experiments and analysis</li>
              <li><strong>Clinical Diagnostics:</strong> Creating standard curves and calibration solutions</li>
              <li><strong>Pharmaceutical Preparation:</strong> Adjusting drug concentrations for different applications</li>
              <li><strong>Quality Control:</strong> Preparing reference standards and controls</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Use calibrated measuring devices</li>
              <li><strong>Documentation:</strong> Record all dilution steps</li>
              <li><strong>Validation:</strong> Verify final concentrations when critical</li>
              <li><strong>Safety:</strong> Follow proper handling procedures</li>
            </ul>
            <p className="italic mt-4">
              Note: Always follow your laboratory's standard operating procedures and safety guidelines when preparing dilutions.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DilutionCalculator;